import { useNavigate } from 'react-router-dom';
import { Simulation } from '@/data/mockAgents';
import {
  Stethoscope,
  ArrowRight,
  CircleDashed,
  PlayCircle,
  CheckCircle,
} from 'lucide-react';

interface SimulationListViewProps {
  simulations: Simulation[];
}

export function SimulationListView({ simulations }: SimulationListViewProps) {
  const navigate = useNavigate();

  const statusConfig = {
    'not-started': {
      color: 'bg-gray-100 text-gray-700',
      icon: <CircleDashed size={16} />,
      text: 'Not Started',
    },
    'in-progress': {
      color: 'bg-blue-100 text-blue-700',
      icon: <PlayCircle size={16} />,
      text: 'In Progress',
    },
    completed: {
      color: 'bg-green-100 text-green-700',
      icon: <CheckCircle size={16} />,
      text: 'Completed',
    },
  };

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted border-b border-border">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-semibold text-card-foreground">
                Agent
              </th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-card-foreground">
                Type
              </th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-card-foreground">
                Specialty
              </th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-card-foreground">
                Status
              </th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-card-foreground">
                Progress
              </th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-card-foreground">
                Best Score
              </th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-card-foreground">
                Sessions
              </th>
              <th className="px-6 py-4 text-right text-sm font-semibold text-card-foreground">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {simulations.map((sim) => (
              <tr
                key={sim.id}
                className="hover:bg-muted/50 transition-colors cursor-pointer"
                onClick={() => navigate(`/simulation/${sim.id}`)}
              >
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                      {sim.imageUrl ? (
                        <img 
                          src={sim.imageUrl} 
                          alt={sim.name} 
                          className="w-full h-full object-cover" 
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-primary-subtle to-primary/10 flex items-center justify-center">
                          <span className="text-lg font-bold text-primary">
                            {sim.initials}
                          </span>
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="font-semibold text-card-foreground">
                        {sim.name}
                      </div>
                      <div className="text-sm text-muted-foreground">{sim.title}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  {sim.platform && (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      sim.platform === 'anam' 
                        ? 'bg-purple-100 text-purple-700' 
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {sim.platform === 'anam' ? '🎥 Video' : '🎙️ Audio'}
                    </span>
                  )}
                </td>
                <td className="px-6 py-4">
                  <span className="inline-flex items-center gap-1 text-sm text-card-foreground">
                    <Stethoscope size={16} />
                    {sim.specialty || '—'}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span
                    className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                      statusConfig[sim.status].color
                    }`}
                  >
                    {statusConfig[sim.status].icon}
                    {statusConfig[sim.status].text}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="w-32">
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className="bg-primary h-2 rounded-full transition-all duration-500"
                        style={{
                          width: `${
                            ((sim.userBestScore || 0) / sim.goalScore) * 100
                          }%`,
                        }}
                      />
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="text-lg font-bold text-card-foreground">
                    {sim.userBestScore || '-'}/{sim.goalScore}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className="text-card-foreground">{sim.sessionsCompleted}</span>
                </td>
                <td className="px-6 py-4 text-right">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/simulation/${sim.id}`);
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary-light transition-colors"
                  >
                    {sim.status === 'not-started' ? 'Start' : 'Continue'}
                    <ArrowRight size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
